import { Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { CatalogService } from './catalog.service';
import {
  OfferingProfileFieldDef,
  fieldLabelKey,
  getProfileSchema,
  mergeProfileModel,
  parseProfilePayload,
  serializeProfilePayload
} from './offering-profile-schemas';

export interface TranslatedProfileField extends OfferingProfileFieldDef {
  label: string;
  selectOptions: { label: string; value: string }[];
}

@Component({
  standalone: true,
  selector: 'ain-offering-profile-form',
  imports: [
    FormsModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    SelectModule,
    MultiSelectModule,
    TextareaModule
  ],
  template: `
    @if (translatedFields().length === 0) {
      <p class="hint">{{ t('catalog.profiles.unsupportedProfileType') }}</p>
    } @else {
      <section class="profile-form-grid">
        @for (field of translatedFields(); track field.key) {
          <label [class.full]="field.type === 'textarea' || field.type === 'offering-multi'">
            <span>
              {{ field.label }}
              @if (field.required) {
                <em class="req">*</em>
              }
            </span>
            @switch (field.type) {
              @case ('boolean') {
                <div class="bool-row">
                  <p-checkbox [binary]="true" [(ngModel)]="model[field.key]" (ngModelChange)="emitChange()" />
                </div>
              }
              @case ('number') {
                <p-inputNumber
                  [(ngModel)]="model[field.key]"
                  [useGrouping]="false"
                  mode="decimal"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="4"
                  (ngModelChange)="emitChange()" />
              }
              @case ('select') {
                <p-select
                  [(ngModel)]="model[field.key]"
                  [options]="field.selectOptions"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  (ngModelChange)="emitChange()" />
              }
              @case ('offering-multi') {
                <p-multiselect
                  [(ngModel)]="model[field.key]"
                  [options]="offeringOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  filterBy="label"
                  display="chip"
                  appendTo="body"
                  (ngModelChange)="emitChange()" />
              }
              @case ('textarea') {
                <textarea pInputTextarea rows="3" [(ngModel)]="model[field.key]" (ngModelChange)="emitChange()"></textarea>
              }
              @default {
                <input pInputText [(ngModel)]="model[field.key]" (ngModelChange)="emitChange()" />
              }
            }
          </label>
        }
      </section>
    }
  `,
  styles: [`
    .profile-form-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 10px;
    }
    .profile-form-grid label {
      display: grid;
      gap: 6px;
    }
    .profile-form-grid label.full {
      grid-column: 1 / -1;
    }
    .profile-form-grid span {
      color: #475569;
      font-size: 12px;
      font-weight: 600;
    }
    .req { color: #b91c1c; font-style: normal; }
    .bool-row { min-height: 2.5rem; display: flex; align-items: center; }
    .hint { color: #64748b; font-size: 13px; margin: 0; }
  `]
})
export class OfferingProfileFormComponent {
  private readonly localization = inject(LocalizationService);
  private readonly catalog = inject(CatalogService);

  readonly profileType = input.required<string>();
  readonly payloadJson = model<string>('{}');

  model: Record<string, unknown> = {};
  readonly offeringOptions = signal<{ label: string; value: string }[]>([]);

  readonly translatedFields = computed(() => {
    const type = this.profileType();
    this.localization.resources();
    this.localization.activeCulture();
    return getProfileSchema(type).map(field => this.translateField(field));
  });

  constructor() {
    this.catalog.listOfferings().subscribe({
      next: rows => this.offeringOptions.set(
        rows.filter(row => row.inventoryEnabled).map(row => ({
          label: `${row.code} — ${row.title}`,
          value: row.id
        }))
      ),
      error: () => this.offeringOptions.set([])
    });

    effect(() => {
      const type = this.profileType();
      const json = this.payloadJson();
      this.model = mergeProfileModel(type, parseProfilePayload(json));
    });
  }

  emitChange(): void {
    this.payloadJson.set(serializeProfilePayload(this.model));
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private translateField(field: OfferingProfileFieldDef): TranslatedProfileField {
    return {
      ...field,
      label: this.t(fieldLabelKey(field.key)),
      selectOptions: (field.options ?? []).map(option => ({
        value: option.value,
        label: this.t(option.labelKey)
      }))
    };
  }
}
